import React from "react";
import { useState, useEffect } from "react";
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
} from "@material-tailwind/react";
import { Typography, Input, Button, Textarea } from "@material-tailwind/react";
import { PlusCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import Select from 'react-select';
import Toaster from "../../components/Toaster";
import { useSelector, useDispatch } from "react-redux";
import { addCategory, resetStatus, unsetSelectedCategory, getMainCategory, getCategory, getSubCategory } from "@/store/slices/categorySlice";
import ShowLoader from "@/components/Loader";
import FilePreview from "@/components/FilePreview";
import ScrollToTop from "@/components/ScrollToTop";
import { useTranslation } from "react-i18next";

const noHTMLRegex = /^(?!.*<[^>]*>).*$/;
const noCodeRegex = /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;
const notBlankRegex = /\S/;

export function AddCategory({ open, handler, parent_id, setSelectedCategoryId, setSelectedMainCategoryId }) {
    const { t } = useTranslation("category");
    const schema = yup.object({
        name: yup
            .string()
            .required(t('validations.category_name_required'))
            .test(
                "not-only-whitespace",
                t('validations.invalid_name'),
                (value) => value && notBlankRegex.test(value)
            )
            .test(
                "no-html",
                t('validations.no_html'),
                (value) => value && noHTMLRegex.test(value)
            )
            .test(
                "no-code",
                t('validations.no_code'),
                (value) => value && noCodeRegex.test(value)
            )
            .min(3, t('validations.name_min'))
            .max(30, t('validations.name_max')),
    
        description: yup
            .string()
            .max(255, t('validations.description_max'))
            .test(
                "not-only-whitespace",
                t('validations.description_no_spaces'),
                (value) => !value || notBlankRegex.test(value)
            )
            .test(
                "no-html",
                t('validations.no_html'),
                (value) => !value || noHTMLRegex.test(value)
            )
            .test(
                "no-code",
                t('validations.no_code'),
                (value) => !value || noCodeRegex.test(value)
            ),
    
        parent_id: yup
            .string()
            .required(t('validations.main_category_required')),
    
        image: yup
            .mixed()
            .required(t('validations.category_image_required'))
            .test(
                "fileType",
                t('validations.invalid_file_type'),
                (value) => !value || !(value instanceof File) || value.type.startsWith("image/")
            ),
    });
    const dispatch = useDispatch();
    const [isError, setIsError] = useState("");
    const [parentCategories, setParentCategories] = useState([]);
    const { list_main_categories } = useSelector((state) => state.category);
    const selectedCategory = useSelector((s) => s.category.selected);

    const { loading } = useSelector((state) => state.category);

    const defaultValues = {
        id: null,
        parent_id: null,
        name: null,
        description: "",
        status: "active",
        image: null,
    };

    const { register, handleSubmit, control, setValue, watch, formState: { errors }, reset } = useForm({
        resolver: yupResolver(schema),
        defaultValues: defaultValues
    });

    useEffect(() => {
        if (selectedCategory && selectedCategory.id) {
            reset({ ...selectedCategory });
        } else {
            reset(defaultValues);
            setValue('parent_id', parent_id, { shouldValidate: true });
        }
        if (list_main_categories?.main_category?.length) {
            const options = list_main_categories.main_category.map(cat => ({ label: cat.name, value: cat.id }));
            setParentCategories(options);
        }
    }, [selectedCategory, dispatch, parent_id, list_main_categories]);


    const image = watch("image");

    const close = () => {
        handler();
        reset(defaultValues);
        setTimeout(() => {
            dispatch(unsetSelectedCategory());
        }, 1000);
    }

    const onSubmit = (data) => {
        dispatch(addCategory(data))
            .unwrap()
            .then((result) => {
                if (result.status) {
                    close();
                    dispatch(getMainCategory());
                    setSelectedCategoryId(result?.data?.id);
                    setSelectedMainCategoryId(result?.data?.parent_id);
                    dispatch(getCategory(result?.data?.parent_id));
                    dispatch(getSubCategory(result?.data?.id));
                    let msg = data?.id ? t('category_updated_successfully') : t('category_created_successfully');
                    Toaster.success(msg);
                } else {
                    setIsError(result.message || t('something_went_wrong'));
                    setTimeout(() => {
                        setIsError('');
                    }, 8000);
                    dispatch(resetStatus());
                }
            })
            .catch((err) => {
                setIsError(err?.message || t('failed_to_add_update_category'));
                setTimeout(() => {
                    setIsError('');
                }, 8000);
                dispatch(resetStatus());
            });
    };

    const removeImage = () => {
        setValue("image", null, { shouldValidate: false });
    }


    return (
        <Dialog open={open} handler={close}>
            {loading && <ShowLoader />}
            <ScrollToTop />
            <div className="max-h-[100vh] overflow-y-auto px-4 pt-4 pb-6">
                <DialogHeader className="mt-3">{selectedCategory?.id ? t('edit_category') : t('create_category')}</DialogHeader>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogBody>
                        {isError &&
                            <div class="p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400" role="alert">
                                <span class="font-medium">{t('oops')}</span> {isError}
                            </div>
                        }
                        <div className="grid grid-cols-1 xl:grid-cols-1 gap-6">
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('select_main_category')}  <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t('main_category')}
                                    onChange={(cat) => {
                                        setValue("parent_id", cat?.value, { shouldValidate: true });
                                    }}
                                    options={parentCategories}
                                    value={parentCategories.find(opt => opt.value == watch("parent_id")) || null}
                                    isClearable
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('category_name')}  <span className="text-red-500">*</span>
                                </Typography>
                                <Input
                                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                    labelProps={{
                                        className: "before:content-none after:content-none",
                                    }}
                                    placeholder={t('enter_category_name')}
                                    type="text"
                                    {...register("name")}
                                />
                                {errors.name && <p className="text-red-500 text-sm">{errors.name?.message}</p>}
                            </div>
                            <div className="flex flex-col gap-1">
                                <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                    {t('description')}
                                </Typography>
                                <Textarea
                                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                    labelProps={{
                                        className: "before:content-none after:content-none",
                                    }}
                                    placeholder={t('enter_description')}
                                    type="text"
                                    {...register("description")}
                                />
                                {errors.description && <p className="text-red-500 text-sm">{errors.description?.message}</p>}
                            </div>
                            <div className="flex flex-col gap-1">
                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                                    <div className="flex flex-col gap-1">
                                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                            {t('upload_category_image')} <span className="text-red-500">*</span>
                                        </Typography>
                                        <label
                                            htmlFor="categoryImage"
                                            className="h-11 w-58 h-28 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border-2 border-dashed border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                                        >
                                            <input
                                                id="categoryImage"
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                {...register("image")}
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) {
                                                        setValue("image", file, { shouldValidate: true });
                                                    }
                                                }}
                                            />
                                            <div className="flex flex-col items-center justify-center cursor-pointer">
                                                <PlusCircleIcon className="h-6 w-6 mb-1" />
                                                <p className="text-sm font-medium">{t('upload_logo')}</p>
                                            </div>
                                        </label>
                                        <p className="text-red-500 text-sm mt-1">{errors.image?.message}</p>
                                    </div>
                                    <div className="flex flex-col gap-1 mt-4">
                                        {image && <FilePreview
                                        file={image}
                                        onRemove={() => removeImage()}
                                        isView={false}
                                        containerClass="mt-4 relative w-58 h-28 rounded-md overflow-hidden border border-gray-300"
                                        />}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </DialogBody>
                    <DialogFooter>
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={close}
                                className="rounded-lg"
                            >
                                {t('cancel')}
                            </Button>
                            <Button
                                type="submit"
                                disabled={loading}
                                className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white btn-material">
                                {loading ? t('saving') : selectedCategory?.id ? t('update') : t('save')}
                            </Button>

                        </div>
                    </DialogFooter>
                </form>
            </div>
        </Dialog>
    );
}

export default AddCategory;
